import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Activity, Target, X } from "lucide-react";

function StatCard({ value, label, color = "text-white" }) {
  return (
    <div className="glass-effect p-3 rounded-lg text-center">
      <div className={`text-xl sm:text-2xl font-bold mono ${color}`}>{value}</div>
      <div className="text-[10px] text-gray-400">{label}</div>
    </div>
  );
}

function TradeRow({ trade, onClose, onDelete }) {
  return (
    <div className="glass-effect p-3 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${trade.signal.includes('BUY') ? 'bg-[#00FFA3]/20 text-[#00FFA3]' : 'bg-[#FF3B30]/20 text-[#FF3B30]'}`}>{trade.signal}</span>
          <span className="text-sm font-bold text-white mono">{trade.symbol}</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
            trade.status === 'open' ? 'bg-[#00E0FF]/20 text-[#00E0FF]' :
            trade.status === 'won' ? 'bg-[#00FFA3]/20 text-[#00FFA3]' :
            trade.status === 'lost' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' :
            'bg-gray-500/20 text-gray-400'
          }`}>{trade.status.toUpperCase()}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold mono ${trade.pnl_percent >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]'}`}>
            {trade.pnl_percent >= 0 ? '+' : ''}{trade.pnl_percent}%
          </span>
          {trade.status === 'open' && (
            <Button
              data-testid={`close-trade-${trade.id}`}
              size="sm"
              variant="ghost"
              onClick={() => onClose(trade.id, trade.current_price)}
              className="text-xs text-gray-400 hover:text-white h-6 px-2"
            >
              Close
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(trade.id)}
            className="text-gray-600 hover:text-[#FF3B30] h-6 px-1"
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs text-gray-400">
        <span>Entry: <span className="text-[#00E0FF] mono">${trade.entry_price}</span></span>
        <span>Current: <span className="text-white mono">${trade.current_price}</span></span>
        <span>Target: <span className="text-[#00FFA3] mono">${trade.target_price}</span></span>
        <span>SL: <span className="text-[#FF3B30] mono">${trade.stop_loss}</span></span>
      </div>
    </div>
  );
}

export default function PerformanceTracker({ perfStats, trades, onClose, onDelete }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="mt-8"
    >
      <Card className="glass-effect">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded' }}>
            <Activity className="w-5 h-5 text-[#F59E0B]" />
            PERFORMANCE TRACKER
          </CardTitle>
        </CardHeader>
        <CardContent>
          {perfStats && perfStats.total_trades > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <StatCard value={perfStats.total_trades} label="Total Trades" />
                <StatCard
                  value={`${perfStats.win_rate}%`}
                  label="Win Rate"
                  color={perfStats.win_rate >= 50 ? "text-[#00FFA3]" : "text-[#FF3B30]"}
                />
                <StatCard
                  value={`${perfStats.total_pnl >= 0 ? "+" : ""}$${perfStats.total_pnl}`}
                  label="Total P&L"
                  color={perfStats.total_pnl >= 0 ? "text-[#00FFA3]" : "text-[#FF3B30]"}
                />
                <StatCard
                  value={`${perfStats.avg_return >= 0 ? "+" : ""}${perfStats.avg_return}%`}
                  label="Avg Return"
                  color={perfStats.avg_return >= 0 ? "text-[#00FFA3]" : "text-[#FF3B30]"}
                />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                <div className="glass-effect p-3 rounded-lg">
                  <div className="text-xs text-gray-400 mb-1">Wins / Losses</div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#00FFA3] font-bold mono">{perfStats.wins}W</span>
                    <span className="text-gray-500">/</span>
                    <span className="text-[#FF3B30] font-bold mono">{perfStats.losses}L</span>
                  </div>
                </div>
                {perfStats.best_trade && (
                  <div className="glass-effect p-3 rounded-lg">
                    <div className="text-xs text-gray-400 mb-1">Best Trade</div>
                    <div className="text-[#00FFA3] font-bold text-sm mono">
                      {perfStats.best_trade.symbol} +{perfStats.best_trade.pnl_percent}%
                    </div>
                  </div>
                )}
                {perfStats.worst_trade && (
                  <div className="glass-effect p-3 rounded-lg">
                    <div className="text-xs text-gray-400 mb-1">Worst Trade</div>
                    <div className="text-[#FF3B30] font-bold text-sm mono">
                      {perfStats.worst_trade.symbol} {perfStats.worst_trade.pnl_percent}%
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-6 text-gray-500 text-sm mb-4">
              <Target className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              No trades yet. Click "Take Trade" on any signal to start tracking.
            </div>
          )}

          {trades.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">Trade History</h4>
              <div className="space-y-2">
                {trades.map(trade => (
                  <TradeRow key={trade.id} trade={trade} onClose={onClose} onDelete={onDelete} />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
